
import React, { useState } from 'react';
import Button from '../../../components/base/Button';
import { useTranslation } from 'react-i18next';

const ContactSection: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    service: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const setCookie = (name: string, value: string, days = 365) => {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; Expires=${expires}; Path=/; SameSite=Lax`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const phoneReg = /^1\d{10}$/;
    const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const cookies = Object.fromEntries(
      document.cookie.split(';').map(v => {
        const [k, ...rest] = v.trim().split('=');
        return [k, decodeURIComponent(rest.join('='))];
      })
    );
    const lastSubmittedAt = cookies['consult_submitted_at'];
    if (lastSubmittedAt) {
      const last = new Date(lastSubmittedAt).getTime();
      if (!Number.isNaN(last) && Date.now() - last < 5 * 60 * 1000) {
        alert('您刚刚提交过，请稍后再试');
        return;
      }
    }
    if (formData.message.length > 500) {
      alert('留言内容不能超过500字符');
      return;
    }
    if (!phoneReg.test(formData.phone)) {
      alert('请输入有效的手机号');
      return;
    }
    if (formData.email && !emailReg.test(formData.email)) {
      alert('请输入有效的邮箱地址');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/consult/CreateConsult', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const json = await response.json();
        const consultId = json?.data?.consult?.id?.toString?.() ?? '';
        setCookie('consult_id', consultId || 'unknown');
        setCookie('consult_name', formData.name || '');
        setCookie('consult_submitted_at', new Date().toISOString());
        setSubmitStatus('success');
        setShowSuccessModal(true);
        setFormData({
          name: '',
          company: '',
          phone: '',
          email: '',
          service: '',
          budget: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.methodsTitle')}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">{t('contact.phoneTitle')}</h4>
                    <a href="tel:19866724796" className="text-gray-600 hover:text-blue-600">19866724796</a>
                    <p className="text-sm text-gray-500">{t('contact.hours')}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">{t('contact.mailTitle')}</h4>
                    <a href="mailto:qbhy0715@qq.com" className="text-gray-600 hover:text-blue-600">qbhy0715@qq.com</a>
                    <p className="text-sm text-gray-500">{t('contact.reply')}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">{t('contact.addressTitle')}</h4>
                    <p className="text-gray-600">广州市黄埔区伴河路96号自编一栋首层8097房</p>
                    <p className="text-sm text-gray-500">欢迎预约实地考察</p>
                  </div>
                </div>

              </div>
            </div>

            {/* Map */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <iframe
                src="https://maps.google.com/maps?q=广州市黄埔区伴河路96号自编一栋首层8097房&z=15&output=embed"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="公司位置"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.formTitle')}</h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.name')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder={t('contact.form.namePlaceholder')}
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.company')}</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder={t('contact.form.companyPlaceholder')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.phone')}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder={t('contact.form.phonePlaceholder')}
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.email')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder={t('contact.form.emailPlaceholder')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.service')}</label>
                  <div className="relative">
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none pr-8"
                    >
                      <option value="">{t('contact.form.servicePlaceholder')}</option>
                      <option value="网站开发">{t('services.items.web.title')}</option>
                      <option value="小程序开发">{t('services.items.mini.title')}</option>
                      <option value="APP开发">{t('services.items.app.title')}</option>
                      <option value="区块链开发">{t('contact.form.blockchain')}</option>
                      <option value="PHP到Go迁移">{t('services.items.migration.title')}</option>
                      <option value="技术咨询">{t('contact.form.consulting')}</option>
                      <option value="其他">{t('contact.form.other')}</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.budget')}</label>
                  <div className="relative">
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none pr-8"
                    >
                      <option value="">{t('contact.form.budgetPlaceholder')}</option>
                      <option value="5万以下">{t('contact.form.budgetRanges.0')}</option>
                      <option value="5-10万">{t('contact.form.budgetRanges.1')}</option>
                      <option value="10-20万">{t('contact.form.budgetRanges.2')}</option>
                      <option value="20-50万">{t('contact.form.budgetRanges.3')}</option>
                      <option value="50万以上">{t('contact.form.budgetRanges.4')}</option>
                      <option value="面议">{t('contact.form.budgetRanges.5')}</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.message')}</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder={t('contact.form.messagePlaceholder')}
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                    </div>
                    {t('contact.form.submitting')}
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-send-plane-fill"></i>
                    </div>
                    {t('contact.form.submit')}
                  </>
                )}
              </Button>

              {/* Submit Status */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
                  <div className="flex items-center">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-check-line"></i>
                    </div>
                    提交成功！我们会在24小时内与您联系。
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                  <div className="flex items-center">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-error-warning-line"></i>
                    </div>
                    提交失败，请稍后重试或直接联系我们。
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-11/12 max-w-md p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
              <i className="ri-check-line text-2xl"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-2">提交成功</h4>
            <p className="text-gray-600 mb-6">感谢您的咨询，我们将在24小时内与您联系。</p>
            <Button
              variant="primary"
              size="md"
              onClick={() => setShowSuccessModal(false)}
              className="w-full"
            >
              我知道了
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ContactSection;
